import { classNames, renderElement } from './dom';

type DisplayableNumber = {
  approximate: string;
  condensed: string;
  negative: boolean;
  scale: string;
  scaleAbbreviation: string;
  value: number;
};

type FetchOptions = {
  endpoint?: string;
};

export type Thermometer = {
  amount: DisplayableNumber;
  amountRemaining: DisplayableNumber;
  endDate: Date;
  goal: DisplayableNumber;
  name: string;
  progress: number;
  startDate: Date;
  type: 'dollars';
  updatedAt: Date;
};

type ThermometerRenderer = (thermometer: Thermometer) => HTMLElement;

type WindowWith$ = Window & {
  $?: {
    getJSON: (url: string) => Promise<any>;
  };
};

export const fetch = async (pageName: string, options: FetchOptions = {}) => {
  if (typeof pageName !== 'string') {
    throw new Error(
      `FLMPiano.thermometers.fetch expects the first argument to be a string, received: ${JSON.stringify(
        pageName,
      )}`,
    );
  }

  const $ = (<WindowWith$>window).$;
  if (!$) {
    throw new Error(
      `FLMPiano.thermometers.fetch: '$' is not available on the window`,
    );
  }

  const endpoint = options.endpoint || 'https://static.theintercept.com';
  const url = `${endpoint}/actionkit/thermometers/${pageName}/data.json`;

  const data: Thermometer = await $.getJSON(url);

  return data;
};

const GRADUATED_BAR_TICKS = [25, 50, 75];
export const renderGraduatedBar: ThermometerRenderer = (
  thermometer: Thermometer,
) =>
  renderElement(
    'div',
    {
      className: classNames(
        'Thermometer',
        'Thermometer--graduated-bar',
        thermometer.progress === 0 && 'Thermometer--empty',
        thermometer.progress >= 100 && 'Thermometer--full',
      ),
    },
    renderElement(
      'div',
      { className: 'Thermometer-box' },
      renderElement('div', {
        className: 'Thermometer-bar',
        style: {
          width: `${thermometer.progress}%`,
        },
      }),
      ...GRADUATED_BAR_TICKS.map((tick) =>
        renderElement('div', {
          className: 'Thermometer-tick',
          style: {
            left: `${tick}%`,
          },
        }),
      ),
    ),
    renderElement(
      'div',
      { className: 'Thermometer-goal' },
      `${thermometer.goal.condensed}${thermometer.goal.scaleAbbreviation}`,
    ),
  );

export const renderMinimalBar: ThermometerRenderer = (
  thermometer: Thermometer,
) =>
  renderElement(
    'div',
    {
      className: classNames(
        'Thermometer',
        'Thermometer--minimal-bar',
        thermometer.progress === 0 && 'Thermometer--empty',
        thermometer.progress >= 100 && 'Thermometer--full',
      ),
    },
    renderElement(
      'div',
      { className: 'Thermometer-box' },
      renderElement('div', {
        className: 'Thermometer-bar',
        style: {
          width: `${thermometer.progress}%`,
        },
      }),
    ),
    renderElement(
      'div',
      { className: 'Thermometer-goal' },
      `${thermometer.goal.condensed}${thermometer.goal.scaleAbbreviation}`,
    ),
  );

const thermometerRenderers: {
  [style: string]: ThermometerRenderer | undefined;
} = {
  'graduated-bar': renderGraduatedBar,
  'minimal-bar': renderMinimalBar,
};
const supportedStyles = Object.keys(thermometerRenderers);

export const render = (thermometer: Thermometer, style: string) => {
  if (typeof thermometer !== 'object') {
    throw new Error(
      `FLMPiano.thermometers.render expects the first argument to be thermometer data, received: ${JSON.stringify(
        thermometer,
      )}`,
    );
  }

  if (typeof style !== 'string') {
    throw new Error(
      `FLMPiano.thermometers.render expects the second argument to be one of [ ${supportedStyles.join(
        ', ',
      )} ], received: ${JSON.stringify(style)}`,
    );
  }

  const renderer = thermometerRenderers[style];
  if (!renderer) {
    throw new Error(
      `FLMPiano.thermometers.render does not support the '${style}'. Supported styles: [ ${supportedStyles.join(
        ', ',
      )} ]`,
    );
  }

  return renderer(thermometer);
};
